import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Car, 
  LogOut, 
  Search, 
  FileText, 
  Users, 
  CreditCard,
  CheckCircle,
  Clock,
  AlertCircle,
  Eye,
  Download,
  RefreshCw,
  Send,
  Settings,
  MessageSquare,
  Edit,
  Save,
  X,
  Hash,
  Truck,
  Phone,
  Trash2
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [activeTab, setActiveTab] = useState("dossiers");
  const [editMode, setEditMode] = useState(false);
  const [editedDossier, setEditedDossier] = useState(null);
  const [messageToClient, setMessageToClient] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [callbackRequests, setCallbackRequests] = useState([]);
  const [loadingCallbacks, setLoadingCallbacks] = useState(false);
  const [dossierInfo, setDossierInfo] = useState({
    cpi_number: "",
    ww_provisoire: "",
    immatriculation_definitive: "",
    admin_message: ""
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      const [dossiersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/dossiers`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() })
      ]);
      
      setDossiers(dossiersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expirée");
        navigate('/admin');
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCallbackRequests = async () => {
    setLoadingCallbacks(true);
    try {
      const res = await axios.get(`${API}/admin/callback-requests`, { headers: getAuthHeaders() });
      setCallbackRequests(res.data);
    } catch (error) {
      toast.error("Erreur de chargement des demandes de rappel");
    } finally {
      setLoadingCallbacks(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
    fetchCallbackRequests();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin');
  };

  const updateCallbackStatus = async (requestId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/callback-requests/${requestId}?status=${newStatus}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Statut mis à jour");
      fetchCallbackRequests();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const deleteCallbackRequest = async (requestId) => {
    if (!window.confirm("Supprimer cette demande de rappel ?")) return;
    try {
      await axios.delete(
        `${API}/admin/callback-requests/${requestId}`,
        { headers: getAuthHeaders() }
      );
      toast.success("Demande supprimée");
      fetchCallbackRequests();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const updateStatus = async (dossierId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/dossiers/${dossierId}`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      
      toast.success("Statut mis à jour");
      fetchData();
      if (selectedDossier && selectedDossier.id === dossierId) {
        setSelectedDossier(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const updateDossierInfo = async () => {
    if (!selectedDossier) return;
    
    try {
      await axios.put(
        `${API}/admin/dossiers/${selectedDossier.id}/info`,
        dossierInfo,
        { headers: getAuthHeaders() }
      );
      
      toast.success("Informations mises à jour");
      fetchData();
      setSelectedDossier(prev => ({
        ...prev,
        ...dossierInfo
      }));
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const saveDossierEdits = async () => {
    if (!editedDossier) return;
    
    try {
      await axios.put(
        `${API}/admin/dossiers/${editedDossier.id}`,
        { 
          status: editedDossier.status,
          notes: editedDossier.admin_notes 
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success("Modifications enregistrées");
      setEditMode(false);
      fetchData();
      setSelectedDossier(editedDossier);
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const sendMessageToClient = async () => {
    if (!messageToClient.trim() || !selectedDossier) {
      toast.error("Veuillez saisir un message");
      return;
    }
    
    // Save admin message to dossier
    try {
      await axios.put(
        `${API}/admin/dossiers/${selectedDossier.id}/info`,
        { admin_message: messageToClient },
        { headers: getAuthHeaders() }
      );
    } catch (error) {
      // Continue even if save fails
    }
    
    setSendingMessage(true);
    // Simulated - in real app would send email
    setTimeout(() => {
      toast.success(`Message envoyé à ${selectedDossier.client.email}`);
      setMessageToClient("");
      setSendingMessage(false);
    }, 1000);
  };

  const downloadCerfa = async (dossierId, cerfaType) => {
    try {
      const response = await axios.get(
        `${API}/dossiers/${dossierId}/cerfa/${cerfaType}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cerfa_${cerfaType}_${dossierId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`CERFA ${cerfaType} téléchargé`);
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const filteredDossiers = dossiers.filter(d => {
    const matchesSearch = 
      d.client.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.client.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.vehicle.marque.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const newCallbacksCount = callbackRequests.filter(r => r.status === 'nouveau').length;

  const getStatusBadge = (status) => {
    const config = {
      'en_attente': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'payé': { color: 'bg-blue-100 text-blue-800', icon: CreditCard },
      'en_cours': { color: 'bg-purple-100 text-purple-800', icon: RefreshCw },
      'documents_manquants': { color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
      'terminé': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'rejeté': { color: 'bg-red-100 text-red-800', icon: AlertCircle }
    };
    
    const cfg = config[status] || config['en_attente'];
    const Icon = cfg.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
        <Icon className="w-3 h-3" />
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  const getCallbackStatusBadge = (status) => {
    const config = {
      'nouveau': { color: 'bg-red-100 text-red-800', label: 'Nouveau' },
      'contacté': { color: 'bg-yellow-100 text-yellow-800', label: 'Contacté' },
      'traité': { color: 'bg-green-100 text-green-800', label: 'Traité' }
    };
    const cfg = config[status] || config['nouveau'];
    return (
      <Badge className={cfg.color}>{cfg.label}</Badge>
    );
  };

  const openDossierModal = (dossier) => {
    setSelectedDossier(dossier);
    setEditedDossier({ ...dossier });
    setEditMode(false);
    setMessageToClient("");
    setDossierInfo({
      cpi_number: dossier.cpi_number || "",
      ww_provisoire: dossier.ww_provisoire || "",
      immatriculation_definitive: dossier.immatriculation_definitive || "",
      admin_message: dossier.admin_message || ""
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-[#000091] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-[#000091] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <Car className="w-6 h-6 text-[#000091]" />
              </div>
              <div>
                <h1 className="font-bold">Administration</h1>
                <p className="text-xs text-white/70">Carte Grise Étrangère</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-white/70">
                {localStorage.getItem('admin_email')}
              </span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleLogout}
                className="text-white hover:bg-white/10"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#000091]/10 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-[#000091]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total dossiers</p>
                    <p className="text-2xl font-bold text-[#0F172A]">{stats.total_dossiers}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Payés</p>
                    <p className="text-2xl font-bold text-green-600">{stats.paid_dossiers}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">En attente</p>
                    <p className="text-2xl font-bold text-yellow-600">{stats.pending_dossiers}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Terminés</p>
                    <p className="text-2xl font-bold text-[#0F172A]">{stats.completed_dossiers}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="dossiers" className="data-[state=active]:bg-[#000091] data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />
              Dossiers
            </TabsTrigger>
            <TabsTrigger value="callbacks" className="data-[state=active]:bg-[#000091] data-[state=active]:text-white relative">
              <Phone className="w-4 h-4 mr-2" />
              Demandes de rappel
              {newCallbacksCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {newCallbacksCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#000091] data-[state=active]:text-white">
              <Settings className="w-4 h-4 mr-2" />
              Paramètres
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dossiers" className="space-y-6">
            {/* Filters */}
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Rechercher par nom, email, véhicule..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                      <SelectValue placeholder="Filtrer par statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="en_attente">En attente</SelectItem>
                      <SelectItem value="payé">Payé</SelectItem>
                      <SelectItem value="en_cours">En cours</SelectItem>
                      <SelectItem value="documents_manquants">Documents manquants</SelectItem>
                      <SelectItem value="terminé">Terminé</SelectItem>
                      <SelectItem value="rejeté">Rejeté</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    onClick={fetchData}
                    className="border-[#000091] text-[#000091]"
                    data-testid="refresh-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Dossiers Table */}
            <Card className="border-slate-200" data-testid="dossiers-table-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-[#0F172A]">
                  Dossiers ({filteredDossiers.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Référence</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Véhicule</TableHead>
                        <TableHead>Formule</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredDossiers.map((dossier) => (
                        <TableRow key={dossier.id} className="hover:bg-slate-50">
                          <TableCell className="font-mono text-xs">
                            {dossier.id.slice(0, 8).toUpperCase()}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-[#0F172A]">
                                {dossier.client.prenom} {dossier.client.nom}
                              </p>
                              <p className="text-xs text-slate-500">{dossier.client.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-[#0F172A]">
                                {dossier.vehicle.marque} {dossier.vehicle.modele}
                              </p>
                              <p className="text-xs text-slate-500 font-mono">
                                {dossier.vehicle.numero_serie}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className={`text-xs font-medium px-2 py-1 rounded ${
                              dossier.package_type === 'premium' 
                                ? 'bg-[#000091]/10 text-[#000091]' 
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {dossier.package_type === 'premium' ? 'W + Déf.' : 'Définitive'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(dossier.status)}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {new Date(dossier.created_at).toLocaleDateString('fr-FR')}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDossierModal(dossier)}
                              data-testid={`view-dossier-${dossier.id.slice(0, 8)}-btn`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredDossiers.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                            Aucun dossier trouvé
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Demandes de rappel Tab */}
          <TabsContent value="callbacks" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Phone className="w-5 h-5" />
                      Demandes de rappel
                    </CardTitle>
                    <CardDescription>
                      Clients potentiels souhaitant être rappelés (véhicules hors UE)
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchCallbackRequests}
                    disabled={loadingCallbacks}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingCallbacks ? 'animate-spin' : ''}`} />
                    Actualiser
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingCallbacks ? (
                  <div className="text-center py-8 text-slate-500">Chargement...</div>
                ) : callbackRequests.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Phone className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Aucune demande de rappel</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Nom</TableHead>
                          <TableHead>Téléphone</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Message</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {callbackRequests.map((request) => (
                          <TableRow key={request.id} className={request.status === 'nouveau' ? 'bg-red-50' : ''}>
                            <TableCell className="whitespace-nowrap text-sm text-slate-500">
                              {new Date(request.created_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </TableCell>
                            <TableCell className="font-medium">{request.nom}</TableCell>
                            <TableCell>
                              <a 
                                href={`tel:${request.telephone}`} 
                                className="text-[#000091] hover:underline flex items-center gap-1"
                              >
                                <Phone className="w-3 h-3" />
                                {request.telephone}
                              </a>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {request.email || '-'}
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate text-sm text-slate-600" title={request.message}>
                              {request.message || '-'}
                            </TableCell>
                            <TableCell>
                              {getCallbackStatusBadge(request.status)}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Select 
                                  value={request.status} 
                                  onValueChange={(value) => updateCallbackStatus(request.id, value)}
                                >
                                  <SelectTrigger className="w-28 h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="nouveau">Nouveau</SelectItem>
                                    <SelectItem value="contacté">Contacté</SelectItem>
                                    <SelectItem value="traité">Traité</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => deleteCallbackRequest(request.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Paramètres du service</CardTitle>
                <CardDescription>Configuration des messages et notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#0F172A]">Messages par défaut</h3>
                  
                  <div className="space-y-2">
                    <Label>Message - Documents manquants</Label>
                    <Textarea 
                      defaultValue="Bonjour, certains documents sont manquants dans votre dossier. Merci de compléter les pièces suivantes : [LISTE]. Cordialement, L'équipe Carte Grise Étrangère"
                      className="min-h-[100px]"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Message - Dossier validé</Label>
                    <Textarea 
                      defaultValue="Bonjour, votre dossier a été validé et transmis à la préfecture. Vous recevrez votre carte grise sous 5 à 7 jours. Cordialement, L'équipe Carte Grise Étrangère"
                      className="min-h-[100px]"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Message - Certificat de conformité requis</Label>
                    <Textarea 
                      defaultValue="Bonjour, les informations présentes sur votre certificat d'immatriculation étranger ne permettent pas d'obtenir une carte grise définitive (TVV incomplet ou numéro de réception européen incomplet). Merci de nous fournir un Certificat de Conformité Européen (COC). Cordialement, L'équipe Carte Grise Étrangère"
                      className="min-h-[100px]"
                    />
                  </div>
                </div>

                <Button className="bg-[#000091] hover:bg-[#000091]/90">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les paramètres
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Dossier Detail Modal */}
      <Dialog open={!!selectedDossier} onOpenChange={() => setSelectedDossier(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedDossier && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-[#0F172A]">
                    Dossier #{selectedDossier.id.slice(0, 8).toUpperCase()}
                  </DialogTitle>
                  <div className="flex items-center gap-2">
                    {!editMode ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditMode(true)}
                        className="border-[#000091] text-[#000091]"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Modifier
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditMode(false);
                            setEditedDossier({ ...selectedDossier });
                          }}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Annuler
                        </Button>
                        <Button
                          size="sm"
                          onClick={saveDossierEdits}
                          className="bg-[#000091] hover:bg-[#000091]/90"
                        >
                          <Save className="w-4 h-4 mr-1" />
                          Enregistrer
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <DialogDescription>
                  Créé le {new Date(selectedDossier.created_at).toLocaleDateString('fr-FR')}
                </DialogDescription>
              </DialogHeader>

              <Tabs defaultValue="details" className="mt-4">
                <TabsList className="bg-slate-100">
                  <TabsTrigger value="details">Détails</TabsTrigger>
                  <TabsTrigger value="immatriculation">Immatriculation</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                  <TabsTrigger value="contact">Contacter</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-6 py-4">
                  {/* Status */}
                  <div>
                    <h4 className="font-semibold text-[#0F172A] mb-2">Statut du dossier</h4>
                    <div className="flex flex-wrap gap-2">
                      {['en_attente', 'payé', 'en_cours', 'documents_manquants', 'terminé', 'rejeté'].map(status => (
                        <Button
                          key={status}
                          variant={(editMode ? editedDossier : selectedDossier).status === status ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            if (editMode) {
                              setEditedDossier(prev => ({ ...prev, status }));
                            } else {
                              updateStatus(selectedDossier.id, status);
                            }
                          }}
                          className={(editMode ? editedDossier : selectedDossier).status === status ? 'bg-[#000091]' : ''}
                        >
                          {status.replace(/_/g, ' ')}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Client info */}
                  <div>
                    <h4 className="font-semibold text-[#0F172A] mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#000091]" />
                      Client
                    </h4>
                    <div className="bg-slate-50 rounded-lg p-4 space-y-1 text-sm">
                      <p><span className="text-slate-500">Nom:</span> {selectedDossier.client.civilite} {selectedDossier.client.prenom} {selectedDossier.client.nom}</p>
                      <p><span className="text-slate-500">Email:</span> {selectedDossier.client.email}</p>
                      <p><span className="text-slate-500">Tél:</span> {selectedDossier.client.telephone}</p>
                      <p><span className="text-slate-500">Adresse:</span> {selectedDossier.client.adresse}, {selectedDossier.client.code_postal} {selectedDossier.client.ville}</p>
                    </div>
                  </div>

                  {/* Vehicle info */}
                  <div>
                    <h4 className="font-semibold text-[#0F172A] mb-2 flex items-center gap-2">
                      <Car className="w-4 h-4 text-[#000091]" />
                      Véhicule
                    </h4>
                    <div className="bg-slate-50 rounded-lg p-4 space-y-1 text-sm">
                      <p><span className="text-slate-500">Marque/Modèle:</span> {selectedDossier.vehicle.marque} {selectedDossier.vehicle.modele}</p>
                      <p><span className="text-slate-500">VIN:</span> <span className="font-mono">{selectedDossier.vehicle.numero_serie}</span></p>
                      <p><span className="text-slate-500">Date 1ère immat:</span> {selectedDossier.vehicle.date_premiere_immat}</p>
                      <p><span className="text-slate-500">Pays:</span> {selectedDossier.vehicle.pays_origine}</p>
                      <p><span className="text-slate-500">Énergie:</span> {selectedDossier.vehicle.energie}</p>
                    </div>
                  </div>

                  {/* Admin Notes */}
                  <div>
                    <h4 className="font-semibold text-[#0F172A] mb-2">Notes internes</h4>
                    {editMode ? (
                      <Textarea
                        value={editedDossier.admin_notes || ''}
                        onChange={(e) => setEditedDossier(prev => ({ ...prev, admin_notes: e.target.value }))}
                        placeholder="Ajouter des notes sur ce dossier..."
                        className="min-h-[100px]"
                      />
                    ) : (
                      <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 min-h-[60px]">
                        {selectedDossier.admin_notes || 'Aucune note'}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="documents" className="space-y-4 py-4">
                  {/* Documents */}
                  <div>
                    <h4 className="font-semibold text-[#0F172A] mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#000091]" />
                      Documents fournis ({Object.keys(selectedDossier.documents || {}).length})
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.keys(selectedDossier.documents || {}).map(doc => (
                        <span key={doc} className="px-3 py-2 bg-green-100 text-green-800 rounded text-sm flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          {doc.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* CERFA Downloads */}
                  <div>
                    <h4 className="font-semibold text-[#0F172A] mb-2">Formulaires CERFA</h4>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadCerfa(selectedDossier.id, '13757')}
                        className="border-[#000091] text-[#000091]"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        CERFA 13757
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadCerfa(selectedDossier.id, '13750')}
                        className="border-[#000091] text-[#000091]"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        CERFA 13750
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="immatriculation" className="space-y-6 py-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <Truck className="w-5 h-5" />
                      Informations d'immatriculation
                    </h4>
                    <p className="text-sm text-blue-700 mb-4">
                      Ces informations seront visibles par le client dans son espace personnel.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>N° CPI (Certificat Provisoire)</Label>
                        <Input
                          value={dossierInfo.cpi_number}
                          onChange={(e) => setDossierInfo(prev => ({ ...prev, cpi_number: e.target.value }))}
                          placeholder="Ex: 123456789"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>WW Provisoire</Label>
                        <Input
                          value={dossierInfo.ww_provisoire}
                          onChange={(e) => setDossierInfo(prev => ({ ...prev, ww_provisoire: e.target.value }))}
                          placeholder="Ex: WW-123-AB"
                        />
                      </div>
                      
                      <div className="space-y-2 md:col-span-2">
                        <Label>Immatriculation définitive</Label>
                        <Input
                          value={dossierInfo.immatriculation_definitive}
                          onChange={(e) => setDossierInfo(prev => ({ ...prev, immatriculation_definitive: e.target.value }))}
                          placeholder="Ex: AB-123-CD"
                        />
                      </div>
                    </div>
                    
                    <Button
                      onClick={updateDossierInfo}
                      className="mt-4 bg-[#000091] hover:bg-[#000091]/90"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer les numéros
                    </Button>
                  </div>

                  {/* Current values display */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card className={`border-slate-200 ${selectedDossier.cpi_number ? 'bg-green-50 border-green-200' : ''}`}>
                      <CardContent className="p-4 text-center">
                        <Hash className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                        <p className="text-xs text-slate-500 mb-1">N° CPI</p>
                        <p className="font-mono font-bold text-sm">
                          {selectedDossier.cpi_number || '-'}
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className={`border-slate-200 ${selectedDossier.ww_provisoire ? 'bg-blue-50 border-blue-200' : ''}`}>
                      <CardContent className="p-4 text-center">
                        <Truck className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                        <p className="text-xs text-slate-500 mb-1">WW Provisoire</p>
                        <p className="font-mono font-bold text-sm">
                          {selectedDossier.ww_provisoire || '-'}
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className={`border-slate-200 ${selectedDossier.immatriculation_definitive ? 'bg-green-50 border-green-200' : ''}`}>
                      <CardContent className="p-4 text-center">
                        <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
                        <p className="text-xs text-slate-500 mb-1">Immat. définitive</p>
                        <p className="font-mono font-bold text-sm">
                          {selectedDossier.immatriculation_definitive || '-'}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="contact" className="space-y-4 py-4">
                  <div>
                    <h4 className="font-semibold text-[#0F172A] mb-2 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-[#000091]" />
                      Envoyer un message au client
                    </h4>
                    <p className="text-sm text-slate-500 mb-3">
                      Un email sera envoyé à {selectedDossier.client.email}
                    </p>
                    
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setMessageToClient("Bonjour, certains documents sont manquants dans votre dossier. Merci de nous fournir : \n\n- \n\nCordialement,\nL'équipe Carte Grise Étrangère")}
                        >
                          Documents manquants
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setMessageToClient("Bonjour, les informations présentes sur votre certificat d'immatriculation étranger ne permettent pas d'obtenir une carte grise définitive (TVV incomplet ou numéro de réception européen incomplet). Merci de nous fournir un Certificat de Conformité Européen (COC).\n\nCordialement,\nL'équipe Carte Grise Étrangère")}
                        >
                          COC requis
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setMessageToClient("Bonjour, votre dossier a été validé et transmis à la préfecture. Vous recevrez votre carte grise sous 5 à 7 jours.\n\nCordialement,\nL'équipe Carte Grise Étrangère")}
                        >
                          Dossier validé
                        </Button>
                      </div>
                      
                      <Textarea
                        value={messageToClient}
                        onChange={(e) => setMessageToClient(e.target.value)}
                        placeholder="Saisissez votre message..."
                        className="min-h-[150px]"
                      />
                      
                      <Button
                        onClick={sendMessageToClient}
                        disabled={sendingMessage || !messageToClient.trim()}
                        className="bg-[#000091] hover:bg-[#000091]/90"
                      >
                        {sendingMessage ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Envoi...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Envoyer le message
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setSelectedDossier(null)}>
                  Fermer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
